/**
 * P8 — N4 · Competência 3 (Geometria) · task-model H1.15.K02
 *
 *   "Construa um trapézio retângulo que tenha AB como uma das bases."
 *
 * O segundo item de construção, e ele mostra uma coisa que o da bissetriz não mostrava:
 * **a resposta certa não é única**. Existem infinitos trapézios retângulos sobre AB —
 * qualquer altura, para qualquer um dos dois lados. Todos são aceitos.
 *
 * Isso só é possível porque a correção é por PREDICADO. Um corretor por gabarito
 * precisaria de uma resposta canônica e reprovaria as outras infinitas; um predicado
 * pergunta "isto é um trapézio retângulo sobre AB?" e pronto.
 *
 * O QUE SE PEDE É O TRAPÉZIO: os vértices C e D E os lados BC, CD e DA. Um lado vale se
 * estiver sobre qualquer traço (ver `ladoTracado`): a perpendicular por A já é o lado
 * AD. O comando diz isso, e a correção e a meta de estrelas seguem o mesmo texto.
 *
 * O predicado tem de fazer uma coisa a mais que o da bissetriz: **descobrir quais** dos
 * pontos do aluno formam o quadrilátero. Ele testa os pares ordenados de pontos
 * criados, o que é barato (poucos pontos) e dispensa o aluno de declarar sua intenção.
 */

import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type {
  ConstructionSnapshot,
  GeoId,
  GeoObject,
  GLine,
  GPoint,
} from '../../geom/construction.ts';
import { addObject, EMPTY_SNAPSHOT, evaluate, geoId } from '../../geom/construction.ts';
import { isParallel, isPerpendicular, isRightTrapezoid } from '../../geom/predicates.ts';
import { DEFAULT_SNAP } from '../../geom/snapping.ts';
import type { Tol } from '../../geom/tolerance.ts';
import { charLengthOf } from '../../geom/tolerance.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { dist, fromAngle, toRad } from '../../geom/vec.ts';
import { constructionToScene } from '../../render/geom-scene.ts';
import type { PredicateOutcome } from '../../schema/evidence.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  CONSTR_INCOMPLETO,
  CONSTR_PARALELOGRAMO,
  CONSTR_SEM_LADOS,
  CONSTR_SEM_PARALELAS,
  CONSTR_TRAPEZIO_SEM_RETO,
} from '../../schema/errors.ts';
import type { ConstructionTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import { ladoTracado, pontosDaConstrucao } from '../_comum/construcao.ts';

const A_ID = geoId('A');
const B_ID = geoId('B');
const AB_ID = geoId('lAB');

const GIVEN_IDS: ReadonlySet<string> = new Set(['A', 'B', 'lAB']);

export interface Params {
  /** Comprimento de AB. */
  readonly base: number;
  /** Inclinação de AB em graus — muda a orientação espacial a cada seed. */
  readonly inclinacao: number;
}

/**
 * A inclinação fica longe das direções "fáceis" de propósito. Com AB horizontal, o
 * aluno consegue levantar a perpendicular alinhando pela borda da tela, e o item
 * passaria a medir pontaria em vez de geometria.
 */
function drawParams(rng: Rng): Params {
  const inclinacao = sampleWhere(
    rng.fork('inclinacao'),
    (r) => r.int(0, 359),
    (ang) => {
      for (const facil of [0, 45, 90, 135, 180, 225, 270, 315]) {
        const d = Math.abs(((ang - facil + 180 + 360) % 360) - 180);
        if (d < 14) return false;
      }
      return true;
    },
    { label: 'inclinacao' },
  );

  return { base: rng.int(110, 150), inclinacao };
}

function posicoes(p: Params): { A: Vec2; B: Vec2 } {
  const A: Vec2 = { x: 0, y: 0 };
  const dir = fromAngle(toRad(p.inclinacao), p.base);
  return { A, B: { x: A.x + dir.x, y: A.y + dir.y } };
}

function buildGivens(p: Params): ConstructionSnapshot {
  const { A, B } = posicoes(p);
  let snap = EMPTY_SNAPSHOT;
  snap = addObject(snap, { id: A_ID, kind: 'point', def: { via: 'given' }, name: 'A', at: A });
  snap = addObject(snap, { id: B_ID, kind: 'point', def: { via: 'given' }, name: 'B', at: B });
  snap = addObject(snap, {
    id: AB_ID,
    kind: 'line',
    def: { via: 'through', a: A_ID, b: B_ID, span: 'segment' },
  });
  return snap;
}

// ---------------------------------------------------------------------------
// Predicado
// ---------------------------------------------------------------------------

function par(objs: ReadonlyMap<GeoId, GeoObject>): { A: GPoint; B: GPoint } | null {
  const A = objs.get(A_ID);
  const B = objs.get(B_ID);
  if (!A || !B || A.kind !== 'point' || B.kind !== 'point') return null;
  return { A, B };
}

function retasDoAluno(objs: ReadonlyMap<GeoId, GeoObject>): GLine[] {
  return [...objs.values()].filter(
    (o): o is GLine => o.kind === 'line' && !GIVEN_IDS.has(o.id as string),
  );
}

interface Quadrilatero {
  readonly outcome: PredicateOutcome;
  readonly C: GPoint;
  readonly D: GPoint;
  /** BC, CD e DA estão traçados. */
  readonly lados: boolean;
}

/**
 * Procura, entre os pontos da construção — os marcados E os cruzamentos das retas
 * traçadas (ver `_comum/construcao.ts`) —, um par (C, D) que feche ABCD como trapézio
 * retângulo. Quem levanta a perpendicular por A e traça uma paralela a AB já tem, no
 * cruzamento das duas, o vértice do ângulo reto. Entre os que passam, prefere o que tem
 * os lados traçados; sem nenhum que passe, devolve o que chegou mais perto, para o
 * resíduo alimentar o feedback.
 */
function melhorQuadrilatero(objs: ReadonlyMap<GeoId, GeoObject>, tol: Tol): Quadrilatero | null {
  const p = par(objs);
  if (!p) return null;

  const cands = pontosDaConstrucao(objs, GIVEN_IDS, dist(p.A.pos, p.B.pos));
  if (cands.length < 2) return null;

  const L = dist(p.A.pos, p.B.pos);
  const eps = tol.distRel * L;
  let valido: Quadrilatero | null = null;
  let melhor: Quadrilatero | null = null;

  for (const C of cands) {
    for (const D of cands) {
      if (C.id === D.id) continue;
      // Degenerados: pontos praticamente sobre a reta AB ou colados um no outro.
      if (dist(C.pos, D.pos) / L < 0.08) continue;

      const out = isRightTrapezoid([p.A, p.B, C, D], tol);
      if (out.ok) {
        const lados =
          ladoTracado(objs, p.B.pos, C.pos, eps) &&
          ladoTracado(objs, C.pos, D.pos, eps) &&
          ladoTracado(objs, D.pos, p.A.pos, eps);
        if (lados) return { outcome: out, C, D, lados };
        valido ??= { outcome: out, C, D, lados };
        continue;
      }
      if (!melhor || out.residual < melhor.outcome.residual)
        melhor = { outcome: out, C, D, lados: false };
    }
  }

  return valido ?? melhor;
}

const alvo: ConstructionTarget = {
  describe: [
    txt('Construa um '),
    strong('trapézio retângulo'),
    txt(' ABCD que tenha o segmento AB como uma das bases — isto é, com CD paralelo a AB '),
    txt('e dois ângulos retos —, com os vértices C e D e os lados BC, CD e DA traçados.'),
  ],

  predicate(objs, _givens, tol) {
    const r = melhorQuadrilatero(objs, tol);
    if (!r) {
      return {
        name: 'isRightTrapezoid',
        ok: false,
        residual: Infinity,
        unit: 'deg',
        tolerance: tol.angleDeg,
        message: [txt('Ainda não há dois pontos novos que possam fechar um quadrilátero com AB.')],
      };
    }
    if (r.outcome.ok && !r.lados) {
      return {
        ...r.outcome,
        ok: false,
        residual: 1,
        message: [
          txt(
            'Os vértices C e D formam um trapézio retângulo com A e B, mas faltam lados: BC, CD e DA precisam estar traçados.',
          ),
        ],
      };
    }
    return r.outcome;
  },

  diagnostics: [
    {
      // Os vértices estão certos; faltou traçar algum lado.
      error: CONSTR_SEM_LADOS,
      test(objs, _g, tol) {
        const r = melhorQuadrilatero(objs, tol);
        return r !== null && r.outcome.ok && !r.lados;
      },
    },
    {
      // Os dois pares de lados opostos ficaram paralelos.
      error: CONSTR_PARALELOGRAMO,
      test(objs, _g, tol) {
        const r = melhorQuadrilatero(objs, tol);
        if (!r) return false;
        const p = par(objs);
        if (!p) return false;
        // AD ∥ BC além de AB ∥ DC ⇒ paralelogramo, não trapézio.
        const AD = linhaEntre(p.A.pos, r.D.pos);
        const BC = linhaEntre(p.B.pos, r.C.pos);
        return isParallel(AD, BC, tol).ok;
      },
    },
    {
      // Trapézio de verdade, mas sem os ângulos retos.
      error: CONSTR_TRAPEZIO_SEM_RETO,
      test(objs, _g, tol) {
        const r = melhorQuadrilatero(objs, tol);
        if (!r) return false;
        const p = par(objs);
        if (!p) return false;
        const AB = linhaEntre(p.A.pos, p.B.pos);
        const DC = linhaEntre(r.D.pos, r.C.pos);
        return isParallel(AB, DC, tol).ok;
      },
    },
    {
      error: CONSTR_SEM_PARALELAS,
      test(objs, _g, tol) {
        const r = melhorQuadrilatero(objs, tol);
        return r !== null && !r.outcome.ok;
      },
    },
    {
      error: CONSTR_INCOMPLETO,
      test: () => true,
    },
  ],

  robustnessTrials: 20,

  partialCredit: [
    {
      id: 'perpendicular',
      label: 'Traçou uma perpendicular a AB',
      weight: 0.3,
      test(objs, _g, tol) {
        const p = par(objs);
        if (!p) return false;
        const AB = linhaEntre(p.A.pos, p.B.pos);
        return retasDoAluno(objs).some((l) => isPerpendicular(l, AB, tol).ok);
      },
    },
    {
      id: 'paralela',
      label: 'Traçou uma paralela a AB',
      weight: 0.3,
      test(objs, _g, tol) {
        const p = par(objs);
        if (!p) return false;
        const AB = linhaEntre(p.A.pos, p.B.pos);
        return retasDoAluno(objs).some((l) => isParallel(l, AB, tol).ok);
      },
    },
    {
      id: 'dois-pontos',
      label: 'Obteve os vértices C e D de um trapézio retângulo',
      weight: 0.2,
      test(objs, _g, tol: Tol) {
        return melhorQuadrilatero(objs, tol)?.outcome.ok === true;
      },
    },
    {
      id: 'lados',
      label: 'Traçou os lados BC, CD e DA',
      weight: 0.2,
      test(objs, _g, tol: Tol) {
        return melhorQuadrilatero(objs, tol)?.lados === true;
      },
    },
  ],
};

/** Adaptador: monta um `GLine` mínimo a partir de dois pontos, para usar os predicados. */
function linhaEntre(a: Vec2, b: Vec2): GLine {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L = Math.hypot(dx, dy) || 1;
  return {
    id: geoId('tmp'),
    kind: 'line',
    def: { via: 'through', a: A_ID, b: B_ID, span: 'segment' },
    name: '',
    p: a,
    dir: { x: dx / L, y: dy / L },
    span: 'segment',
    q: b,
  };
}

// ---------------------------------------------------------------------------

export const trapezioRetangulo: ItemTemplate<Params, ConstructionSnapshot> = {
  id: templateId('h1-15-K02-trapezio'),
  // v3: o trapézio pedido inclui os lados — no comando, na correção e na meta.
  version: 3,
  taskModel: taskModelCode('H1.15.K02'),
  skills: [skillCode('H1.15')],
  axis: 'II',
  level: 'N4',
  title: 'Construir um trapézio retângulo',
  // Crédito parcial escalonado: GPCM, não 3PL.
  responseModel: 'GPCM',

  difficultySources: ['construction.open-start', 'construction.no-hint', 'steps.3plus'],

  params: drawParams,
  solve: buildGivens,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Inclinações longe dos eixos tornam impossível "acertar pela borda da tela".
    const desvio = Math.min(
      ...[0, 45, 90, 135, 180, 225, 270, 315].map((f) =>
        Math.abs(((p.inclinacao - f + 180 + 360) % 360) - 180),
      ),
    );
    if (desvio > 25) fontes.push('construction.free-tools');
    return fontes;
  },

  build(p) {
    const givens = buildGivens(p);
    const { A, B } = posicoes(p);
    const L = charLengthOf([A, B]);

    const desc =
      'Segmento AB desenhado em posição oblíqua no plano de trabalho. A tarefa é construir ' +
      'um trapézio retângulo que tenha AB como uma de suas bases.';

    const cena = constructionToScene(evaluate(givens), {
      title: 'Segmento AB',
      desc,
      givenIds: GIVEN_IDS,
    });

    return {
      // O que é o trapézio pedido e o que conta como resposta ficam no enunciado; o
      // comando diz só o que fazer. Estava tudo no comando — três orações de regras
      // antes do verbo, e o aluno chegava à ação sem saber qual era a principal.
      stem: [
        txt('A figura mostra o segmento AB. Queremos um '),
        strong('trapézio retângulo'),
        txt(' ABCD em que AB e CD sejam as bases paralelas. Há '),
        strong('infinitos'),
        txt(' trapézios assim, e qualquer um deles serve.'),
      ],
      comando: [
        txt('Usando as ferramentas disponíveis, marque os '),
        strong('vértices'),
        txt(' C e D e trace os '),
        strong('lados'),
        txt(' BC, CD e DA. Um lado pode ser um segmento ou parte de uma reta traçada.'),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'segmento',
          scene: cena,
          alt: desc,
        },
      ],
      interaction: {
        kind: 'construct',
        toolbelt: [
          'select',
          'point',
          'segment',
          'line',
          'perpendicular',
          'parallel',
          'circle-center-through',
          'intersect',
          'undo',
          'delete',
        ],
        givens,
        perturbable: [
          { id: A_ID, radius: 0.12 * L },
          { id: B_ID, radius: 0.12 * L },
        ],
        target: alvo,
        snapping: { ...DEFAULT_SNAP, pixelRadius: 14 },
        // A perpendicular a AB por A e a paralela a AB por um ponto do lado de B: os
        // vértices que faltam são esse ponto e o cruzamento das duas (as duas retas já são os
        // lados AD e DC); e o segmento BC. Três traços.
        meta: 3,
      },
      dicas: [
        [
          txt(
            'Num trapézio retângulo, as duas bases são paralelas e um dos outros lados é perpendicular a elas.',
          ),
        ],
        [txt('Comece pela perpendicular a AB passando por A: o lado AD fica sobre ela.')],
        [
          txt(
            'A outra base passa por D e é paralela a AB. Se ela tiver o mesmo comprimento de AB, a figura vira um retângulo.',
          ),
        ],
      ],
      rationale: [
        strong('Uma construção possível, entre infinitas. '),
        txt('1) Trace a perpendicular a AB passando por A. '),
        txt('2) Marque D sobre essa perpendicular, à altura que quiser. '),
        txt('3) Trace a paralela a AB passando por D. '),
        txt('4) Marque C sobre essa paralela, a uma distância de D diferente de AB. '),
        txt('5) Trace o segmento BC. A perpendicular e a paralela já são os lados AD e DC.'),
        br,
        txt(
          'Assim AB ∥ DC (por construção), o ângulo em A é reto (AD ⊥ AB) e o ângulo em D ' +
            'também é (DC ∥ AB e AD ⊥ AB). Como DC ≠ AB, o quadrilátero é trapézio e não ' +
            'paralelogramo.',
        ),
        br,
        txt(
          'Se DC ficar igual a AB, o resultado é um retângulo: os dois pares de lados ficam ' +
            'paralelos, e pela definição usada na escola isso deixa de ser trapézio.',
        ),
      ],
      declared: 'dificil',
    };
  },
};
